import { ref, computed, onMounted, watch } from 'vue'

const THEME_STORAGE_KEY = 'linkflare_theme'

// Global theme state — 'light' | 'dark' | 'auto'
const themePreference = ref('auto')
// Resolved system preference for when themePreference is 'auto'
const systemIsDark = ref(false)

// Whether the effective theme is dark (resolves 'auto' via system)
const isDarkMode = computed(() => {
  if (themePreference.value === 'auto') return systemIsDark.value
  return themePreference.value === 'dark'
})

export function useTheme() {
  const theme = computed(() => isDarkMode.value ? 'dark' : 'light')

  const toggleTheme = () => {
    // Cycle: light → dark → auto → light
    if (themePreference.value === 'light') themePreference.value = 'dark'
    else if (themePreference.value === 'dark') themePreference.value = 'auto'
    else themePreference.value = 'light'
    saveThemePreference()
    applyTheme()
  }

  const setTheme = (themeName) => {
    themePreference.value = themeName
    saveThemePreference()
    applyTheme()
  }

  const saveThemePreference = () => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themePreference.value)
    } catch (e) {
      console.warn('Failed to save theme preference to localStorage', e)
    }
  }

  const loadThemePreference = () => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY)
      if (saved === 'light' || saved === 'dark' || saved === 'auto') {
        themePreference.value = saved
      } else {
        // No saved preference or legacy value → default to auto
        themePreference.value = 'auto'
      }
      // Always read current system preference
      if (window.matchMedia) {
        systemIsDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
      }
    } catch (e) {
      console.warn('Failed to load theme preference from localStorage', e)
      themePreference.value = 'auto'
    }
  }

  const applyTheme = () => {
    const root = document.documentElement
    if (isDarkMode.value) {
      root.classList.add('dark-theme')
      root.classList.remove('light-theme')
    } else {
      root.classList.add('light-theme')
      root.classList.remove('dark-theme')
    }
    syncThemeColorMeta()
  }

  // Browser/PWA chrome must follow the APPLIED theme, not the OS: the static
  // media-gated meta pair in index.html (and the manifest theme_color) only
  // covers the pre-JS paint. A stored preference can disagree with the OS, so
  // after every class flip both metas are set to the resolved page background.
  const syncThemeColorMeta = () => {
    const bg = getComputedStyle(document.documentElement)
      .getPropertyValue('--bg-primary')
      .trim()
    if (!bg) return // token not resolvable (tests / style.css absent) — keep the static pair
    document.querySelectorAll('meta[name="theme-color"]').forEach(meta => {
      meta.setAttribute('content', bg)
    })
  }

  // Initialize theme on composable creation
  const initializeTheme = () => {
    loadThemePreference()
    applyTheme()

    // Listen for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleSystemThemeChange = (e) => {
        systemIsDark.value = e.matches
        // Re-apply if currently in auto mode
        if (themePreference.value === 'auto') {
          applyTheme()
        }
      }
      
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleSystemThemeChange)
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleSystemThemeChange)
      }
    }
  }

  return {
    isDarkMode,
    themePreference: computed(() => themePreference.value),
    theme,
    toggleTheme,
    setTheme,
    initializeTheme
  }
}
